
import { EXPLORER_MAP, } from "@bracket/platform/constants";
import { formatAddress, } from "@bracket/platform/formatters";

import type { EthereumAddress, ValidChainId } from "@bracket/types";

import { getTheme, Box, ClipboardText, ExternalLinkIcon, Tooltip, } from "@bracket/ui";
import styled from "styled-components";



export declare type EthAddressProps = {
	address: EthereumAddress;
	className?: string;
	chainId?: ValidChainId;
	explorer?: boolean;
	justify?: "start" | "end" | "center";
	size?: string;
	full?: boolean;
	color?: string;
	order?: "REVERSE" | "NORMAL";
	multiline?: boolean;
	bold? : boolean;
}




/**
 * A component that displays an Ethereum address with copy-to-clipboard functionality
 * 
 * @param {EthAddressProps} props - The component props
 * @param {EthereumAddress} props.address - The Ethereum address to display
 * @param {string} [props.className] - Optional CSS class name for styling
 * @param {ValidChainId} [props.chainId] - The chain ID for explorer link generation
 * @param {boolean} [props.explorer] - Whether to show explorer link
 * @param {"start" | "end" | "center"} [props.justify] - Horizontal alignment of the component
 * @param {string} [props.size] - Font size CSS value for the component
 * @param {boolean} [props.full] - Whether to show the full address instead of formatted version
 * @returns {JSX.Element} A ClipboardText component displaying the formatted address
 */
export const EthAddress = ({ address, className, chainId = 1, explorer, justify = "center", size, full, color, order = "NORMAL", multiline = false, bold = false, }: EthAddressProps) => {
	const exp = chainId && EXPLORER_MAP.get(chainId);
	const justifyValue = justify === "start" ? "flex-start" : justify === "end" ? "flex-end" : "center";
	const displayValue = full ? address : formatAddress(address);
	className = multiline ? `${typeof className === "string" ? className : ""} multiline` : className;
	return (
		<Brace $justify={justifyValue} $size={size} $order={order} $bold={bold}>
			<ClipboardText 
				value={displayValue} 
				actual={address} 
				className={className}
				color={color}
			/>
			{ explorer && exp && (
				<ExplorerLink
					href={`${exp.url}/address/${address}`}
					target="_blank"
					rel="noopener noreferrer"
					onClick={(e) => e.stopPropagation()}
				>
					<ExternalLinkIcon tooltip={`View on ${exp.name}`} position="top" />
				</ExplorerLink>
			)}
		</Brace>
	);
};


const Brace = styled(Box)<{ $justify: string; $size?: string; $order?: "REVERSE" | "NORMAL"; $bold?: boolean; }>`
	align-items: center;
	justify-content: ${props => props.$justify};
	gap: 8px;
	font-family: ${() => getTheme().fonts.body};
	& * {
		font-family: ${() => getTheme().fonts.body} !important;
	}
	${({ $bold }) => $bold && `font-weight: 600;`}
	${props => props.$size && `font-size: ${props.$size};`}
	${props => props.$order === "REVERSE" && `flex-direction: row-reverse;`}
`;

const ExplorerLink = styled.a`
	display: inline-flex;
	text-decoration: none;
	color: ${() => getTheme().heading};
	transition: color 0.3s ease;
	&:hover {
		text-decoration: underline;
		color: ${() => getTheme().primary};
	}
	svg {
		scale: 0.8;
		transform-origin: left center;
	}
`;
